/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import window from 'ember-window-mock';

const SAVE_SETTINGS_ERROR_MESSAGE = 'Could not update your account settings. Please try again.';

export default class PrivacyControls extends Component {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;

  get privacyControls() {
    return Object.freeze([
      {
        id: 'necessaryFunctionality',
        label: 'Necessary functionality',
        description: "These cookies are essential to the app's experience and functionality. These cannot be disabled.",
        readOnly: true,
        active: true,
        task: 'updateNecessaryFunctionalityTask',
      },
      {
        id: 'enableTroubleshootingCookies',
        label: 'Performance and troubleshooting',
        description: `
  <p>These cookies help us with finding and fixing bugs, solving your support requests faster,
  and improving the overall experience.</p>
  <p>If disabled, it will limit our ability to troubleshoot any issues you experience.</p>`,
        readOnly: !this.auth.hasPermission('account/edit'),
        disabledTooltip: 'Only administrators can change this setting.',
        task: 'confirmTroubleshootingCookiesPreferenceTask',
      },
    ]);
  }

  @tracked isShowingTroubleshootingCookiesPreferenceConfirmationModal = false;
  @tracked existingTroubleshootingCookiesAccountPreference?: boolean;

  // The necessaryFunctionality toggle is readOnly, so this is an empty task.
  updateNecessaryFunctionalityTask = dropTask(async () => {});

  confirmTroubleshootingCookiesPreferenceTask = dropTask(async () => {
    this.setExistingTroubleshootingCookiesPreference();
    this.isShowingTroubleshootingCookiesPreferenceConfirmationModal = true;
  });

  toggleTroubleshootingCookiesTask = dropTask(async () => {
    let account = this.account.current;

    this.analytics.track(
      'account',
      account.enableTroubleshootingCookies
        ? 'privacy_disable_product_tracking_started'
        : 'privacy_enable_product_tracking_started',
    );

    try {
      account.toggleProperty('enableTroubleshootingCookies');
      await account.save();
      this.setExistingTroubleshootingCookiesPreference();
      window.location.reload();
    } catch {
      account.rollbackAttributes();
      this.alerts.error('privacy.update-cookies', SAVE_SETTINGS_ERROR_MESSAGE);
    } finally {
      this.isShowingTroubleshootingCookiesPreferenceConfirmationModal = false;
    }
  });

  private setExistingTroubleshootingCookiesPreference() {
    this.existingTroubleshootingCookiesAccountPreference = this.account.current.enableTroubleshootingCookies;
  }
}
